import * as LucideIcons from 'lucide-react';
import { GetStaticProps } from 'next';
import { groq, PortableText } from 'next-sanity';
import { NextSeo } from 'next-seo';
import React from 'react';
import { PageContainer, PageSectionLarge } from '@/components/base/Sections';
import {
  TypographyH6,
  TypographyHero,
  TypographyLarge,
  TypographyText,
  TypographyTitle,
} from '@/components/base/Typography';
import { portableTextComponents } from '@/components/sanity/PortableTextComponents';
import { sanityClient } from '@/config/sanity';
import { CallToActionSection, ShopByShapeSection } from '@/pages/index';

interface FourC {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const Page = ({ d }: { d: any }) => {
  if (!d) {
    return null;
  }

  const hero = {
    title: d.title || 'Diamond Carat Guide',
    subtitle:
      'Learn about diamond carats and how to choose the perfect size for your budget and style.',
    image: d.mainImage,
    buttons: [],
  };

  const fourCsData: FourC[] = [
    { title: 'Cut', description: "The quality of a diamond's cut", lucideIcon: 'Gem' },
    { title: 'Color', description: 'The absence of color in a diamond', lucideIcon: 'Palette' },
    {
      title: 'Clarity',
      description: 'The absence of inclusions and blemishes',
      lucideIcon: 'Eye',
    },
    { title: 'Carat', description: 'The weight of a diamond', lucideIcon: 'Scale' },
  ].map((c: any) => {
    const Icon = (LucideIcons as any)[c.lucideIcon] || LucideIcons.Circle;
    return { title: c.title, description: c.description, icon: Icon };
  });

  const seoBase = {
    title: 'Dream Diamonds | Diamond Carat Guide',
    description:
      'Learn about diamonds and how to find your perfect diamond with our education centre.',
  };

  return (
    <>
      <NextSeo {...seoBase} />
      <PageContainer className="">
        <div className="flex flex-col">
          <div className="relative w-full bg-gradient-to-r from-slate-800 via-[#0f172b] to-slate-900 py-20 text-white">
            {hero?.image?.asset?.url && (
              <div className="absolute top-0 right-0 h-full w-full sm:w-2/3 lg:w-1/2">
                <img
                  src={hero.image.asset.url}
                  alt={hero.title}
                  className="h-full w-full object-cover object-right opacity-20 sm:opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-slate-900/60 to-slate-900 sm:via-slate-900/20" />
              </div>
            )}
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col items-center md:flex-row">
                <div className="flex w-full flex-col justify-center text-center md:text-left">
                  <TypographyHero className="">{hero?.title}</TypographyHero>
                  <TypographyLarge className="max-w-xl">{hero?.subtitle}</TypographyLarge>
                </div>
              </div>
            </div>
          </div>

          <PageSectionLarge className="">
            <TypographyTitle className="text-center lg:text-left">
              The 4Cs of Diamonds
            </TypographyTitle>
            <TypographyText className="mb-6 text-center sm:mb-10 sm:text-xl lg:text-left">
              The world's most celebrated diamonds are defined by four essential qualities. Discover
              how Cut, Colour, Clarity, and Carat come together to create brilliance that endures.
            </TypographyText>
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-24">
              <div className="order-2 flex justify-center lg:order-1">
                <div className="relative w-full max-w-md sm:max-w-lg">
                  <div className="overflow-hidden rounded-2xl shadow-2xl">
                    <img
                      src="/images/landing/engagement-ring-hand.jpg"
                      alt="Diamond on Hand"
                      width={500}
                      height={500}
                      className="h-auto w-full object-cover"
                    />
                  </div>
                </div>
              </div>
              <div className="order-1 space-y-10 sm:space-y-14 lg:order-2">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-10 lg:gap-12">
                  {fourCsData.map(({ title, description, icon: Icon }) => (
                    <div key={title} className="flex items-start space-x-5">
                      <div className="bg-primary text-primary-foreground flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full">
                        <Icon className="h-7 w-7" />
                      </div>
                      <div>
                        <TypographyH6 className="flex">{title}</TypographyH6>
                        <TypographyText className="text-base opacity-70">
                          {description}
                        </TypographyText>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </PageSectionLarge>

          <ShopByShapeSection />

          {/* Render post body content */}
          {d.body && (
            <PageSectionLarge className="bg-background">
              <div className="prose prose-lg max-w-none">
                <PortableText value={d.body} components={portableTextComponents} />
              </div>
            </PageSectionLarge>
          )}
          <CallToActionSection />
        </div>
      </PageContainer>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const query = groq`
    *[_type == "post" && slug.current == $slug][0]{
      _id,
      title,
      "slug": slug.current,
      body[]{
        ...,
        _type == "image" => {
          ...,
          asset->{
            _id,
            _ref,
            url
          }
        }
      },
      mainImage {
        asset->{
          _id,
          _ref,
          url
        }
      },
      author->{ name },
      categories[]->{title},
      publishedAt
    }
  `;

  const d = await sanityClient.fetch(query, { slug: 'diamond-carat-guide' });

  if (!d) {
    return { notFound: true };
  }

  return {
    props: { d },
    revalidate: 60,
  };
};

export default Page;
